import { percentChangeFromReference } from '@helpers/utils/numbers/numbers';
import { DailyFallbackRange } from '@terminal/hooks/use-price-market-reaction-data/types';
import { usePriceHistoryList } from '@terminal/react-query-hooks/prices/usePriceHistoryList';
import { Tracking } from '@terminal/services/tracking';
import { addDays, startOfDay } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';

export const NEW_YORK_TIMEZONE = 'America/New_York';

interface UsePriceChangeMarketReactionDataProps {
  fromDateEST?: Date;
  toDateEST?: Date;
  tags: string[];
  tickers: string[];
  enabled?: boolean;
  dailyFallbackRange?: DailyFallbackRange;
}

export const usePriceChangeMarketReactionData = ({
  fromDateEST,
  toDateEST,
  tags,
  tickers,
  enabled = true,
  dailyFallbackRange,
}: UsePriceChangeMarketReactionDataProps) => {
  const fromDateUTC = fromDateEST
    ? fromZonedTime(fromDateEST, NEW_YORK_TIMEZONE)
    : undefined;
  const toDateUTC = toDateEST
    ? fromZonedTime(toDateEST, NEW_YORK_TIMEZONE)
    : undefined;

  const priceRange = dailyFallbackRange ?? {
    dateFrom: fromDateUTC?.toISOString() ?? '',
    dateTo: toDateUTC?.toISOString() ?? '',
  };

  const prevCloseRange = {
    dateFrom: dailyFallbackRange
      ? addDays(new Date(priceRange.dateFrom), -5).toISOString()
      : fromDateUTC
        ? addDays(startOfDay(fromDateUTC), -5).toISOString()
        : '',
    dateTo: dailyFallbackRange ? priceRange.dateFrom : priceRange.dateTo,
  };

  const { data: priceDataList, isLoading: isPriceHistoryLoading } =
    usePriceHistoryList({
      tags,
      tickers,
      resolution: dailyFallbackRange ? '1D' : '1m',
      dateFrom: priceRange.dateFrom,
      dateTo: priceRange.dateTo,
      enabled: enabled && !!fromDateUTC && !!toDateUTC,
    });

  const { data: previousPriceDataList, isLoading: isPrevCloseLoading } =
    usePriceHistoryList({
      tags,
      tickers,
      resolution: '1D',
      dateFrom: prevCloseRange.dateFrom,
      dateTo: prevCloseRange.dateTo,
      enabled: enabled && !!fromDateUTC && !!toDateUTC,
    });

  if (isPriceHistoryLoading || isPrevCloseLoading) {
    return {
      data: [],
      isLoading: true,
    };
  }

  const data = priceDataList.map((priceData, index) => {
    const prevClosePrices = previousPriceDataList.find(
      p => p.entityTag === priceData.entityTag
    )?.data;

    if (!prevClosePrices) {
      return {
        data: [],
        lastProportionChange: undefined,
        prevCloseDate: undefined,
      };
    }

    if (!priceData.data?.length) {
      return {
        data: [],
        lastProportionChange: undefined,
        prevCloseDate: undefined,
      };
    }

    const firstDayOfTheWindow = priceData.data[0].time;
    const startOfDayUTC = firstDayOfTheWindow.replace(/T.*/, 'T00:00:00Z');
    const prevCloseData = prevClosePrices.findLast(
      prevClosePrice => prevClosePrice.time < startOfDayUTC
    );
    const data = priceData.data.map(price => {
      if (!prevCloseData) {
        Tracking.captureException(
          `usePriceChangeMarketReactionData - tag: ${tags[index]} - prevCloseData not found`
        );
      }

      const value = percentChangeFromReference(
        price.close,
        prevCloseData?.close
      );

      return {
        time: price.time,
        value,
      };
    });

    const lastKnownPrice = priceData.data[priceData.data.length - 1]?.close;
    const lastProportionChange = percentChangeFromReference(
      lastKnownPrice,
      prevCloseData?.close
    );

    return {
      data,
      lastProportionChange,
      prevCloseData,
    };
  });

  return {
    data,
    isLoading: false,
  };
};
